import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "./Reveal";
import { ProductLogo } from "./ProductLogo";
import {
  featuredProducts,
  secondaryProducts,
  upcomingProducts,
} from "../data/products";

const DEMO_IDS = new Set([
  "playvia",
  "mindbrief",
  "readywork",
  "profitpop",
  "skripsinext",
  "uptoday",
]);

const EXTERNAL_DEMO_URLS = { sebelumsah: "https://sebelumsah.com/" };

const DnaLine = ({ dna, className = "" }) => (
  <div
    className={`flex flex-wrap items-center gap-x-1.5 gap-y-1 font-mono text-[10px] uppercase tracking-[0.12em] text-steel ${className}`}
  >
    {dna.map((step, i) => (
      <span key={step} className="flex items-center gap-1.5">
        {step}
        {i < dna.length - 1 && (
          <span className="text-platinum" aria-hidden>
            →
          </span>
        )}
      </span>
    ))}
  </div>
);

const DemoCta = ({ product }) => {
  const externalUrl = EXTERNAL_DEMO_URLS[product.id];
  if (externalUrl) {
    return (
      <a
        href={externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`demo-cta-${product.id}`}
        className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink transition-colors group-hover:text-steel sm:mt-4"
      >
        View Demo
        <ArrowUpRight
          size={15}
          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </a>
    );
  }
  if (!DEMO_IDS.has(product.id)) return null;
  return (
    <Link
      to={`/demo/${product.id}`}
      data-testid={`demo-cta-${product.id}`}
      className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink transition-colors group-hover:text-steel sm:mt-4"
    >
      View Demo
      <ArrowUpRight
        size={15}
        className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </Link>
  );
};

// Flagship products
const FeaturedCard = ({ product }) => (
  <div
    data-testid={`product-card-${product.id}`}
    className="group grid h-full grid-cols-[1fr_auto] gap-4 rounded-[22px] border border-hairline bg-surface p-6 transition-all duration-300 hover:-translate-y-1 hover:card-shadow sm:gap-6 sm:p-9"
  >
    <div className="flex min-w-0 flex-col">
      <span className="font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
        {product.category}
      </span>
      <h3 className="mt-2 text-[23px] font-bold tracking-tight text-ink sm:mt-2.5 sm:text-[25px]">
        {product.name}
      </h3>
      <p
        className="mt-1.5 text-[13.5px] font-semibold leading-snug text-steel sm:text-[14px]"
      >
        {product.differentiator}
      </p>
      <p className="mt-2 text-[14px] leading-snug text-slatetx sm:mt-2.5 sm:text-[14.5px] sm:leading-relaxed">
        {product.explanation}
      </p>
      <div className="mt-auto pt-4 sm:pt-6">
        <DnaLine dna={product.dna} />
        <DemoCta product={product} />
      </div>
    </div>
    <ProductLogo
      product={product}
      className="h-[80px] w-[80px] shrink-0 self-start transition-transform duration-300 ease-out active:scale-[0.985] motion-safe:group-hover:-translate-y-0.5 motion-safe:group-hover:scale-[1.03] sm:h-[108px] sm:w-[108px]"
    />
  </div>
);

// Secondary active products — compact
const CompactCard = ({ product }) => (
  <div
    data-testid={`product-card-${product.id}`}
    className="group flex h-full flex-col rounded-[18px] border border-hairline bg-surface p-6 transition-all duration-300 hover:-translate-y-1 hover:card-shadow"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <span className="font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
          {product.category}
        </span>
        <h3 className="mt-1.5 text-[18px] font-bold tracking-tight text-ink">
          {product.name}
        </h3>
      </div>
      <ProductLogo
        product={product}
        rounded="rounded-[13px]"
        className="h-11 w-11 shrink-0 text-[17px] transition-transform duration-300 ease-out motion-safe:group-hover:scale-[1.05]"
      />
    </div>

    <p
      className="mt-2 text-[12.5px] font-semibold leading-snug text-steel"
    >
      {product.differentiator}
    </p>

    <div className="mt-auto pt-5">
      <DnaLine dna={product.dna} />
      <DemoCta product={product} />
    </div>
  </div>
);

const UpcomingCard = ({ product }) => (
  <div
    data-testid={`upcoming-card-${product.id}`}
    className="group flex h-full flex-col rounded-[18px] border border-hairline bg-surface p-6 transition-all duration-300 hover:-translate-y-1 hover:card-shadow"
  >
    <div className="flex items-center justify-between">
      <span
        data-testid={`upcoming-badge-${product.id}`}
        className="inline-flex items-center rounded-full border border-hairline bg-mist px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-steel"
      >
        In Development
      </span>
      <ProductLogo
        product={product}
        rounded="rounded-[12px]"
        className="h-10 w-10 shrink-0 text-[12px] transition-transform duration-300 ease-out motion-safe:group-hover:scale-[1.05]"
      />
    </div>

    <span className="mt-5 font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
      {product.category}
    </span>
    <h3 className="mt-2 text-[19px] font-bold tracking-tight text-ink">
      {product.name}
    </h3>
    <p className="mt-2 text-[13.5px] leading-relaxed text-slatetx">
      {product.description}
    </p>
  </div>
);

export const Products = () => {
  return (
    <section id="products" data-testid="products" className="bg-surface">
      <div className="mx-auto max-w-6xl px-6 py-12 md:py-16 lg:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow>Product Portfolio</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-3 font-bold tracking-tight text-ink text-[clamp(1.9rem,4vw,2.25rem)]">
              Products We Build
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-slatetx">
              Products across family life, careers, learning, business,
              productivity, and personal growth.
            </p>
          </Reveal>
        </div>

        {/* Flagship products */}
        <div className="mt-8 grid gap-6 md:mt-10 md:grid-cols-2">
          {featuredProducts.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06} className="h-full">
              <FeaturedCard product={p} />
            </Reveal>
          ))}
        </div>

        {/* Secondary active products — compact */}
        <p className="mb-4 mt-10 font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
          More active products
        </p>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {secondaryProducts.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.05} className="h-full">
              <CompactCard product={p} />
            </Reveal>
          ))}
        </div>

        {/* Yang Sedang Dikembangkan */}
        <div className="mt-14 border-t border-hairline pt-12 md:mt-16">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>What's Next</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h3 className="mt-3 font-bold tracking-tight text-ink text-[clamp(1.5rem,3vw,1.75rem)]">
                Products in Development
              </h3>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-3 max-w-md text-[15px] leading-relaxed text-slatetx">
                A glimpse at what MisiDigi is building next.
              </p>
            </Reveal>
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {upcomingProducts.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06} className="h-full">
                <UpcomingCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
